import {
  ReactNode, useContext, useEffect, useState,
} from 'react';
import shallow from 'zustand/shallow';
import { getElementEntry } from '../editorTools/queries';
import { useTPlateEditorRef } from '../plate';
import {
  ElementBox,
  useHoveredElementEntry,
  useSelectedElementEntry,
} from '../plugins/elementSelection';
import { SlashCommandMenu } from '../plugins/slashCommand';
import { useDreamEditorStore } from '../plugins/store';
import { OverlayContext } from './context';
import { CanvasContext } from '../components/Canvas/context';
import { ToolbarNavbarContainerInsideDropdownEvent } from '../types';

interface OverlaysProps {
  children?: ReactNode
}

export function Overlays({ children }: OverlaysProps) {
  const [isSlashCommandOpen, isResizing] = useDreamEditorStore(
    (state) => [state.isSlashCommandOpen, state.isResizing],
    shallow,
  );
  let selectedElementEntry = useSelectedElementEntry();
  const hoveredElementEntry = useHoveredElementEntry();

  const { scale } = useContext(CanvasContext);

  // This is used as a fallback just in case the selected element
  // in the editor's store isn't the same as the one selected by
  // editor.selection. This can happen when the editor is blurred,
  // then clicked on again (try clicking on empty page guide).
  const editor = useTPlateEditorRef();
  if (!selectedElementEntry) {
    selectedElementEntry = getElementEntry(editor) || null;
  }

  const [showOverlay, setShowOverlay] = useState(true);

  useEffect(() => {
    const handleInsideDropdown = (
      { isInside: isInsideDropdown } : ToolbarNavbarContainerInsideDropdownEvent,
    ) => {
      setShowOverlay(!isInsideDropdown);
    };
    editor.emitter.on('toolbar:toggle-navbar-container-inside-dropdown', handleInsideDropdown);
    return () => {
      editor.emitter.off('toolbar:toggle-navbar-container-inside-dropdown', handleInsideDropdown);
    };
  }, []);

  const { getElement } = useContext(OverlayContext);

  useEffect(() => {
    const overlayEl = getElement();
    if (overlayEl) {
      overlayEl.style.pointerEvents = isResizing ? 'auto' : '';
    }
  });

  return (
    <>
      {children}
      {showOverlay
        && hoveredElementEntry
        && hoveredElementEntry[0] !== selectedElementEntry?.[0] && (
          <ElementBox element={hoveredElementEntry[0]} isHovered referenceScale={scale} />
      )}
      {showOverlay
        && selectedElementEntry
        && (
        <ElementBox
          element={selectedElementEntry[0]}
          isSelected
          isHovered={selectedElementEntry[0] === hoveredElementEntry?.[0]}
          referenceScale={scale}
        />
        )}
      {showOverlay
        && selectedElementEntry
        && isSlashCommandOpen && (
        <SlashCommandMenu element={selectedElementEntry[0]} />
      )}
    </>
  );
}

Overlays.defaultProps = {
  children: undefined,
};
